import { useMemo, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  CircleMarker,
  Tooltip,
  GeoJSON,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../auth/AuthProvider";
import {
  fetchLoadIntensityAggregate,
  type IntensityAggregateLevel,
  type LoadPlantConsumption,
} from "../../../api/loads";
import type {
  AirshedFeatureCollection,
  IntensityTileResponse,
} from "../../model/domain/types";

// Load uses a warm damage palette to read as "harm caused", visually
// distinct from the RE-project benefit palette (which is green/viridis).
// ColorBrewer YlOrRd 7-class; mapped to share-of-impact % thresholds.
function loadDamageColor(share: number): string {
  if (share >= 8) return "#b10026";
  if (share >= 5) return "#e31a1c";
  if (share >= 3) return "#fc4e2a";
  if (share >= 2) return "#fd8d3c";
  if (share >= 1) return "#feb24c";
  if (share >= 0.5) return "#fed976";
  if (share > 0) return "#ffeda0";
  return "#1e293b";
}

// Re-use the default marker icon (matches LocationPicker pattern)
const defaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const FUEL_COLOR: Record<string, string> = {
  coal: "#71717a", // zinc
  ng: "#f59e0b", // amber
  oil: "#c2410c", // orange-700
};

interface Props {
  loadId: string;
  loadLatitude: number;
  loadLongitude: number;
  loadName: string;
  plants: LoadPlantConsumption[];
  scenarioYear?: number | null;
  airsheds?: AirshedFeatureCollection | null;
  intensityTiles?: IntensityTileResponse | null;
}

// "raster" = no choropleth — show the InMAP intensity tiles + plant circles
// + airsheds. Default so the user sees the raw spatial story (and the BTM
// synthetic plant at the load location) before drilling into boundary
// rollups.
type SpatialView = "raster" | IntensityAggregateLevel;

const LEVELS: { value: SpatialView; label: string }[] = [
  { value: "raster", label: "Plants & Tiles" },
  { value: "state", label: "State" },
  { value: "county", label: "County" },
  { value: "cbsa", label: "Metro (CBSA)" },
];

/**
 * Spatial heatmap: render the per-plant consumption attribution on a map.
 *
 * Circles sized by capacity, opacity weighted by consumed_mwh share of the
 * total, colored by fuel type. Datacenter location marked with the default
 * pin. This is the "where the impacts come from" visual that turns the A4
 * per-plant data into the immediately-readable spatial story.
 */
export function LoadSpatialHeatmap({
  loadId,
  loadLatitude,
  loadLongitude,
  loadName,
  plants,
  scenarioYear,
  airsheds,
  intensityTiles,
}: Props) {
  const { token } = useAuth();
  const [view, setView] = useState<SpatialView>("raster");
  const aggregateLevel: IntensityAggregateLevel | null =
    view === "raster" ? null : view;
  const { data: aggregate, isLoading: aggregateLoading } = useQuery({
    queryKey: ["load-intensity-aggregate", loadId, aggregateLevel],
    queryFn: () =>
      fetchLoadIntensityAggregate(loadId, aggregateLevel!, token!),
    enabled: !!token && !!loadId && aggregateLevel !== null,
    staleTime: 60 * 60_000,
    gcTime: 2 * 60 * 60_000,
    retry: 0,
  });
  const aggregateFeatures = useMemo(() => {
    const raw: any[] = aggregate?.features || [];
    return raw
      .filter((f: any) => (f?.properties?.value_sum ?? 0) > 0)
      .sort(
        (a: any, b: any) =>
          (b.properties?.value_sum || 0) - (a.properties?.value_sum || 0),
      )
      .slice(0, 15);
  }, [aggregate]);
  const points = useMemo(
    () =>
      plants.filter((p) => p.latitude != null && p.longitude != null) as Array<
        LoadPlantConsumption & { latitude: number; longitude: number }
      >,
    [plants],
  );
  const totalMwh = useMemo(
    () => points.reduce((s, p) => s + (p.consumed_mwh || 0), 0) || 1,
    [points],
  );
  const maxCap = useMemo(
    () => Math.max(...points.map((p) => p.capacity_mw || 0), 1),
    [points],
  );
  // Map plant_id -> dominant fuel for coloring airshed polygons. A plant
  // can appear under multiple fuels (rare); pick the fuel with the largest
  // attributed MWh.
  const plantFuelById = useMemo(() => {
    const acc = new Map<string, { fuel: string; mwh: number }>();
    for (const p of plants) {
      const k = String(p.plant_id);
      const prev = acc.get(k);
      if (!prev || (p.consumed_mwh || 0) > prev.mwh) {
        acc.set(k, { fuel: p.fuel_type, mwh: p.consumed_mwh || 0 });
      }
    }
    const out = new Map<string, string>();
    acc.forEach((v, k) => out.set(k, v.fuel));
    return out;
  }, [plants]);

  // Fit a useful default extent: load location + ~5° padding (national grid scope).
  const center: [number, number] = [loadLatitude, loadLongitude];

  return (
    <div className="bg-surface-700 rounded-lg shadow border border-border overflow-hidden">
      <div className="px-6 pt-5 pb-2">
        <h2 className="text-lg font-medium text-text-primary">
          Spatial Heatmap &amp; Dispersion
          <span className="block text-xs font-normal text-text-muted mt-0.5">
            Where the impacts come from + where they disperse downwind.
            Circles = generators that ramped to serve this load
            {scenarioYear ? `, ${scenarioYear} grid` : ""} (size = capacity,
            opacity = share of consumption, color = fuel). Translucent
            polygons = InMAP airsheds (~80% PM2.5 intake zones, 2005 met).
          </span>
        </h2>
      </div>
      <div className="px-2 pb-2">
        <div className="h-[440px] w-full">
          <MapContainer
            center={center}
            zoom={5}
            scrollWheelZoom={false}
            style={{ height: "100%", width: "100%", borderRadius: 8 }}
          >
            {/* Keyless Esri dark basemap — Load uses a darker canvas than RE
                projects so the harm/damage palette reads against it. */}
            <TileLayer
              attribution='Tiles &copy; Esri'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
            />

            {/* Phase 4b InMAP intensity heatmap tiles. Hidden entirely
                when the boundary choropleth is on top — the two layers
                fight visually otherwise. */}
            {intensityTiles && !aggregate && (
              <TileLayer
                key={intensityTiles.tile_url_template}
                url={intensityTiles.tile_url_template}
                minZoom={intensityTiles.min_zoom}
                maxZoom={intensityTiles.max_zoom}
                opacity={0.7}
                tileSize={intensityTiles.tile_size}
                attribution="InMAP ISRM v1.2.1 (Tessum, Hill & Marshall 2017)"
              />
            )}

            {/* Boundary choropleth for the selected rollup level. Custom
                renderer (not the shared portfolio ChoroplethLayer) so we
                can swap the tooltip wording to "DALYs caused" and use a
                damage-flavored color ramp. */}
            {aggregate && (
              <GeoJSON
                key={`load-choro-${aggregateLevel}-${aggregate.features?.length || 0}`}
                data={aggregate as any}
                style={(feat: any) => {
                  const p = feat?.properties || {};
                  const share = p.share_pct ?? 0;
                  return {
                    fillColor: loadDamageColor(share),
                    color: "#0f172a",
                    weight: 0.5,
                    fillOpacity: share > 0 ? 0.7 : 0.05,
                    opacity: 0.6,
                  };
                }}
                onEachFeature={(feat: any, layer: any) => {
                  const p = feat.properties || {};
                  const label = p.name ?? p.id ?? "—";
                  const share = p.share_pct ?? 0;
                  const valueStr =
                    share >= 0.1
                      ? `${share.toFixed(1)}%`
                      : share > 0
                        ? `<0.1%`
                        : "0%";
                  const dalysStr =
                    p.dalys != null && p.dalys !== 0
                      ? `<b>DALYs caused: ${p.dalys.toLocaleString(undefined, { maximumFractionDigits: 2 })}</b><br/>`
                      : "";
                  layer.bindTooltip(
                    `<div class="text-xs"><b>${label}</b><br/>` +
                      dalysStr +
                      `Share of load impact: ${valueStr}<br/>` +
                      (p.rank ? `Rank: #${p.rank}<br/>` : "") +
                      `</div>`,
                    { sticky: true },
                  );
                }}
              />
            )}

            {/* Airshed polygons (InMAP-derived) — where the downwind impacts
                disperse. Hidden when the boundary choropleth is showing
                (otherwise the fuel-tinted airsheds bleed through and the
                view reads as a confusing mix of two heatmaps). */}
            {!aggregate && airsheds && airsheds.features?.length > 0 && (
              <GeoJSON
                key={`airsheds-${airsheds.features.length}`}
                data={airsheds as any}
                style={(feature) => {
                  const pid = String(feature?.properties?.plant_id ?? "");
                  const fuel = plantFuelById.get(pid);
                  const color = (fuel && FUEL_COLOR[fuel]) || "#64748b";
                  return {
                    color,
                    fillColor: color,
                    weight: 0.8,
                    opacity: 0.55,
                    fillOpacity: 0.12,
                  };
                }}
              />
            )}
            <Marker position={center} icon={defaultIcon}>
              <Tooltip permanent direction="top" offset={[0, -36]}>
                <span style={{ fontWeight: 600 }}>{loadName}</span>
              </Tooltip>
            </Marker>
            {!aggregate && points.map((p, i) => {
              const share = (p.consumed_mwh || 0) / totalMwh;
              const isOnsite = String(p.plant_id || "").startsWith("BTM-");
              const radius = Math.max(
                4,
                Math.sqrt((p.capacity_mw || 1) / maxCap) * 22,
              );
              return (
                <CircleMarker
                  key={`${p.plant_id}-${p.fuel_type}-${i}`}
                  center={[p.latitude, p.longitude]}
                  radius={isOnsite ? Math.max(radius, 14) : radius}
                  pathOptions={{
                    color: isOnsite ? "#22d3ee" : FUEL_COLOR[p.fuel_type] || "#64748b",
                    fillColor: FUEL_COLOR[p.fuel_type] || "#64748b",
                    fillOpacity: isOnsite ? 0.85 : Math.min(0.85, 0.25 + share * 50),
                    weight: isOnsite ? 3 : 1,
                    dashArray: isOnsite ? "4 2" : undefined,
                  }}
                >
                  <Tooltip direction="top" permanent={isOnsite}>
                    <div style={{ minWidth: 200 }}>
                      <div style={{ fontWeight: 600 }}>
                        {p.plant_name || p.plant_id}
                        {isOnsite && (
                          <span
                            style={{
                              marginLeft: 6,
                              padding: "1px 6px",
                              borderRadius: 4,
                              background: "#0e7490",
                              color: "#ecfeff",
                              fontSize: 9,
                              fontWeight: 700,
                              verticalAlign: "middle",
                            }}
                          >
                            ON-SITE
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: "#666" }}>
                        {p.fuel_type.toUpperCase()} ·{" "}
                        {[p.county, p.state].filter(Boolean).join(", ") ||
                          (isOnsite ? "at datacenter location" : "")}
                      </div>
                      <div style={{ fontSize: 11, marginTop: 4 }}>
                        Capacity: {p.capacity_mw?.toFixed(0) || "—"} MW
                      </div>
                      <div style={{ fontSize: 11 }}>
                        {isOnsite ? "Onsite generation" : "Consumption attributed"}
                        :{" "}
                        {(p.consumed_mwh || 0).toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        })}{" "}
                        MWh ({(share * 100).toFixed(1)}%)
                      </div>
                      {isOnsite && (
                        <div
                          style={{
                            fontSize: 10,
                            marginTop: 4,
                            color: "#0e7490",
                            fontStyle: "italic",
                          }}
                        >
                          Synthetic plant — BTM NG turbine modeled at the
                          load location (ISRM stack-height conservative on
                          hyperlocal peak).
                        </div>
                      )}
                    </div>
                  </Tooltip>
                </CircleMarker>
              );
            })}
          </MapContainer>
        </div>
        <div className="px-4 py-2 flex gap-4 text-xs text-text-muted">
          <span className="inline-flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ background: FUEL_COLOR.coal }}
            />
            Coal
          </span>
          <span className="inline-flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ background: FUEL_COLOR.ng }}
            />
            Natural gas
          </span>
          <span className="inline-flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ background: FUEL_COLOR.oil }}
            />
            Oil
          </span>
          <span className="ml-auto text-text-muted">
            {points.length} plant{points.length !== 1 ? "s" : ""} ·{" "}
            {totalMwh.toLocaleString(undefined, { maximumFractionDigits: 0 })}{" "}
            MWh total
          </span>
        </div>
      </div>

      {/* Boundary rollup — same card as the map, RE-project parity
          (GeneratorAttributionCard pattern). Tabs switch the API query;
          table shows top-N polygons by share. */}
      <div className="border-t border-border">
        <div className="px-6 pt-4 pb-2 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-text-secondary">
              Impact Rollup by Boundary
            </p>
            <p className="text-xs text-text-muted mt-0.5">
              Population-weighted PM2.5 intake summed over each boundary —
              calibrated to your load&apos;s PM-category DALYs.
            </p>
          </div>
          <div className="flex gap-1 text-xs">
            {LEVELS.map((l) => (
              <button
                key={l.value}
                onClick={() => setView(l.value)}
                className={`px-3 py-1 rounded ${
                  view === l.value
                    ? "bg-quantum-500/20 text-quantum-300 border border-quantum-500/40"
                    : "bg-surface-600 text-text-secondary hover:text-text-primary border border-transparent"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
        <div className="px-6 pb-5">
          {view === "raster" && (
            <div className="text-sm text-text-muted">
              Showing raw InMAP intensity tiles + per-plant attribution on
              the map above. Pick a boundary level (State / County / Metro)
              to roll up the same impact totals into populated polygons.
            </div>
          )}
          {view !== "raster" && aggregateLoading && (
            <div className="text-sm text-text-muted">Loading {view}…</div>
          )}
          {view !== "raster" &&
            !aggregateLoading &&
            aggregateFeatures.length === 0 && (
              <div className="text-sm text-text-muted">
                Boundary aggregate for <strong>{view}</strong> not yet
                available — re-run the Load to populate (the Phase 4b
                rebuild writes these alongside the tiles).
              </div>
            )}
          {view !== "raster" && aggregateFeatures.length > 0 && (
            <>
              <div className="text-xs text-text-muted mb-2">
                Top {aggregateFeatures.length} of{" "}
                {(aggregate?.features || []).length}{" "}
                {view === "cbsa" ? "metros" : `${view}s`} by impact share
              </div>
              <table className="w-full text-xs">
                <thead className="text-text-muted">
                  <tr>
                    <th className="text-left pb-2">
                      {view === "cbsa"
                        ? "Metro"
                        : view === "state"
                          ? "State"
                          : "County"}
                    </th>
                    <th className="text-right pb-2">Share</th>
                    <th className="text-right pb-2">DALYs</th>
                    <th className="text-right pb-2">Intensity</th>
                  </tr>
                </thead>
                <tbody>
                  {aggregateFeatures.map((f: any, i: number) => {
                    const p = f.properties || {};
                    const share = p.share_pct || 0;
                    return (
                      <tr
                        key={p.id ?? `${aggregateLevel}-${i}`}
                        className="border-t border-border"
                      >
                        <td className="py-1 text-text-primary">
                          {p.name || p.id || "—"}
                        </td>
                        <td className="py-1 text-right text-text-primary">
                          <span className="inline-flex items-center gap-1">
                            <span
                              className="inline-block h-2 rounded bg-red-400/60"
                              style={{
                                width: `${Math.min(60, share * 1.2)}px`,
                              }}
                            />
                            {share.toFixed(2)}%
                          </span>
                        </td>
                        <td className="py-1 text-right text-text-secondary">
                          {p.dalys != null
                            ? p.dalys.toLocaleString(undefined, {
                                maximumFractionDigits: 2,
                              })
                            : "—"}
                        </td>
                        <td className="py-1 text-right text-text-muted">
                          {p.intensity != null
                            ? p.intensity.toExponential(2)
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
